import { ISplitActionHandler, SplitActionResult } from "./base";
import {
  NormalizedDeliverySelection,
  EnrichedSupplierForDelivery,
} from "../../../../../components/DeliveryOptions/types";
import { CreateManualDeliveryOptionPayload } from "../../../types/estimateConfirmationTypes";
import { extractApiErrorMessage } from "../../../../../components/DeliveryOptions/utils/apiErrorUtils";
import services from "../../../services";

/**
 * Handles the confirmation flow for estimate splits.
 *
 * For each confirm selection:
 * 1. If manual → creates a manual delivery option via API to get a deliveryOptionId
 * 2. Confirms the split via API with the deliveryOptionId
 *
 * Errors from the backend are surfaced using extractApiErrorMessage.
 */
export class SplitConfirmHandler implements ISplitActionHandler {
  async execute(
    estimateNo: string,
    selections: NormalizedDeliverySelection[],
    splitData: EnrichedSupplierForDelivery[],
  ): Promise<SplitActionResult[]> {
    const results: SplitActionResult[] = [];

    const confirmSelections = selections.filter(
      (s) => s.customerAction === "confirm",
    );

    for (const selection of confirmSelections) {
      const supplierSplit = splitData.find(
        (s) => s.splitId === selection.splitId,
      );
      const supplierDisplayName =
        supplierSplit?.supplierName || "remaining items";

      let deliveryOptionId = selection.deliveryOptionId;

      try {
        if (selection.selectionSource === "manual") {
          const payload: CreateManualDeliveryOptionPayload = {
            cptMin: selection.cptMin!,
            cptMax: selection.cptMax!,
            cptSlab: selection.cptSlab!,
            deliveryCost: selection.deliveryCost!,
          };
          const manualResponse =
            await services.createManualDeliveryOptionForEstimateSplit(
              estimateNo,
              selection.splitId,
              payload,
            );
          deliveryOptionId = manualResponse.deliveryOption._id;
        }

        if (!deliveryOptionId) {
          results.push({
            supplierId: selection.supplierId,
            success: false,
            error: "Missing deliveryOptionId for confirmation",
          });
          continue;
        }

        const confirmResponse = await services.confirmEstimateSplit(
          estimateNo,
          selection.splitId,
          { deliveryOptionId },
        );

        results.push({
          supplierId: selection.supplierId,
          success: true,
          order: confirmResponse,
        });
      } catch (error: unknown) {
        const errorMessage = extractApiErrorMessage(
          error,
          `Failed to confirm split for ${supplierDisplayName}`,
        );
        throw new Error(errorMessage);
      }
    }

    return results;
  }
}
